import { useCallback, useEffect, useState } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { COLLECTIONS } from '../firebase/collections';
import { useAuth } from '../context/AuthContext';
import { validateAnalyticsFilters } from '../utils/analyticsFilters';

function normalizeName(name) {
  return name.trim().toLowerCase();
}

export function useAnalyticsViews() {
  const { companyId, user, profile } = useAuth();
  const [views, setViews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) { setLoading(false); return; }

    const q = query(
      collection(db, COLLECTIONS.ANALYTICS_VIEWS),
      where('companyId', '==', companyId),
      orderBy('updatedAt', 'desc'),
    );

    const unsub = onSnapshot(q, (snap) => {
      setViews(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => {
      console.error('[useAnalyticsViews]', err);
      setLoading(false);
    });

    return unsub;
  }, [companyId]);

  const saveView = useCallback(async (name, filters) => {
    if (!companyId || !user) throw new Error('Not authenticated');
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Name required');

    const dup = views.some((v) => normalizeName(v.name) === normalizeName(trimmed));
    if (dup) {
      const err = new Error('Duplicate name');
      err.code = 'DUPLICATE_NAME';
      throw err;
    }

    const now = serverTimestamp();
    const docRef = await addDoc(collection(db, COLLECTIONS.ANALYTICS_VIEWS), {
      companyId,
      name: trimmed.slice(0, 80),
      filters: validateAnalyticsFilters(filters),
      createdBy: user.uid,
      createdByName: profile?.displayName || user.email || 'Admin',
      createdAt: now,
      updatedAt: now,
      lastUsedAt: now,
    });
    return docRef.id;
  }, [companyId, user, profile, views]);

  const renameView = useCallback(async (viewId, name) => {
    const trimmed = name.trim();
    if (!trimmed) throw new Error('Name required');

    const dup = views.some((v) => v.id !== viewId && normalizeName(v.name) === normalizeName(trimmed));
    if (dup) {
      const err = new Error('Duplicate name');
      err.code = 'DUPLICATE_NAME';
      throw err;
    }

    await updateDoc(doc(db, COLLECTIONS.ANALYTICS_VIEWS, viewId), {
      name: trimmed.slice(0, 80),
      updatedAt: serverTimestamp(),
    });
  }, [views]);

  const deleteView = useCallback(async (viewId) => {
    await deleteDoc(doc(db, COLLECTIONS.ANALYTICS_VIEWS, viewId));
  }, []);

  const touchLastUsed = useCallback(async (viewId) => {
    try {
      await updateDoc(doc(db, COLLECTIONS.ANALYTICS_VIEWS, viewId), {
        lastUsedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch {
      // Non-critical
    }
  }, []);

  return { views, loading, saveView, renameView, deleteView, touchLastUsed };
}
